<template>
  <div>
    <v-breadcrumbs :items="items"></v-breadcrumbs>
    <v-col cols="12">
      <v-alert
          v-if="error_alert"
          text
          prominent
          type="error"
          icon="mdi-cloud-alert"
      >{{ error_alert }}</v-alert
      >
      <v-alert
          v-if="success_alert"
          text
          prominent
          type="success"
          icon="mdi-cloud-alert"
      >{{ success_alert }}</v-alert
      >
    </v-col>

    <!-- Date Range Filter -->
    <v-card class="mb-4">
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="filters.start_date"
                    label="Start Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                    hide-details
                ></v-text-field>
              </template>
              <v-date-picker
                  v-model="filters.start_date"
                  @input="startDateMenu = false"
                  :max="filters.end_date || nowDate"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="filters.end_date"
                    label="End Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                    hide-details
                ></v-text-field>
              </template>
              <v-date-picker
                  v-model="filters.end_date"
                  @input="endDateMenu = false"
                  :min="filters.start_date"
                  :max="nowDate"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="4" class="d-flex align-center">
            <v-btn color="primary" @click="applyFilters" class="mr-2">
              Apply
            </v-btn>
            <v-btn @click="resetFilters" outlined>
              Reset
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card-title>
      SMS Delivery report
      <v-spacer></v-spacer>

      <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          dense
          outlined
          @input="handleSearch"
      ></v-text-field>

      <v-col cols="12" sm="6" md="1">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
                depressed
                color="success"
                v-on="on"
                @click="downloadExcelFile"
                :disabled="excelButton"
            >
              <v-icon>mdi-file-excel</v-icon>
            </v-btn>
          </template>
          <span>Download Phone Number</span>
        </v-tooltip>
      </v-col>
    </v-card-title>

    <v-card>
      <v-data-table
          :headers="headers"
          :items="desserts.data"
          :server-items-length="desserts.total"
          :options.sync="options"
          :loading="loading"
          class="elevation-1"
          dense
      >
        <template v-slot:no-data>
          <v-btn color="primary" @click="initialize">Reset</v-btn>
        </template>

        <template v-slot:item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<style scoped>
.layout {
  display: inline-block;
  width: 100%;
}
</style>

<script>
import atshq from "../../../js/setup";
import moment from "moment";

export default {
  data() {
    return {
      error_alert: null,
      success_alert: null,
      excelButton: false,
      search: "",
      searchTimeout: null,
      startDateMenu: false,
      endDateMenu: false,
      nowDate: moment().format('YYYY-MM-DD'),
      loading: true,
      filters: {
        start_date: moment().format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
        search: ''
      },
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['id'],
        sortDesc: [true]
      },
      headers: [
        {
          text: "ID",
          align: "start",
          sortable: true,
          value: "id",
        },
        {
          text: "Patient",
          value: "patient.patient_name",
        },
        {
          text: "Patient ID",
          value: "patient.patient_id",
        },
        { text: "Phone Number", sortable: true, value: "send_number" },
        { text: "SMS Body", sortable: true, value: "sms_content" },
        { text: "Response", value: "response" },
        { text: "Create at", value: "created_at" },
        { text: "Action", value: "actions", sortable: false },
      ],
      desserts: {
        data: [],
        total: 0,
        from: 0,
        to: 0,
        current_page: 1
      },
      items: [
        {
          text: "Dashboard",
          disabled: false,
          href: "/",
        },
        {
          text: "SMS",
          disabled: true,
          href: "patients",
        },
      ],
      returnUrl: atshq.defaults.headers.common.app_URL,
      returnApiBaseUrl: atshq.defaults.headers.common.api_base_URL,
    };
  },

  watch: {
    options: {
      handler() {
        this.fetchSMSReports();
      },
      deep: true
    }
  },

  created() {
    let userGroups = JSON.parse(localStorage.getItem("userGroups")) || [];
    let permittedGroups = [1];
    let hasAccess = userGroups.some(group => permittedGroups.includes(Number(group)));

    if (hasAccess) {
      console.log("Access Granted");
    } else {
      this.loading = false;
      this.excelButton = true;
      this.error_alert = "Invalid Request";
      return false;
    }
    this.initialize();
  },

  methods: {
    initialize() {
      this.fetchSMSReports();
    },

    async fetchSMSReports() {
      this.loading = true;

      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options;

        const params = {
          ...this.filters,
          page: page,
          per_page: itemsPerPage,
          sort_by: sortBy[0],
          sort_dir: sortDesc[0] ? 'desc' : 'asc'
        };

        const response = await atshq.get("reports/sms-report", { params });
        this.desserts = response.data.data;
        this.loading = false;
      } catch (error) {
        console.error("Error fetching SMS reports:", error);
        this.loading = false;
        this.error_alert = "Failed to load SMS reports";
      }
    },

    applyFilters() {
      this.options.page = 1;
      this.fetchSMSReports();
    },

    resetFilters() {
      this.filters = {
        start_date: moment().format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
        search: ''
      };
      this.search = '';
      this.options.page = 1;
      this.fetchSMSReports();
    },

    handleSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.filters.search = this.search;
        this.options.page = 1;
        this.fetchSMSReports();
      }, 500);
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('DD-MM-YYYY HH:mm');
    },

    downloadExcelFile() {
      const params = new URLSearchParams(this.filters);
      window.open(this.returnApiBaseUrl + "print/download-phone-number?" + params.toString());
      this.success_alert = "File downloaded successfully!";
    }
  },
};
</script>